import type { Stop, StopDetails, StopType } from '#domain/Stop.ts';
import type { StopStatus } from '#domain/StopStatus.ts';

import type { ApiBaseStop, ApiDetailedStop, ApiStopType, ApiUpdateStop, ApiWriteStop } from '../model/types.ts';
import { mapCharging, mapChargingToUpdate, mapChargingToWrite } from './chargingAdapter.ts';
import { mapLocation, mapLocationToApi } from './locationAdapter.ts';
import { mapMaterials } from './materialAdapter.ts';

const mapStopType = (type: ApiStopType | string | undefined): StopType => {
    if (type === 'load' || type === 'unload' || type === 'execute') {
        return type;
    }
    return 'unknown';
};

const mapStopStatus = (status: string | undefined): StopStatus | undefined => {
    if (status === 'open' || status === 'reached' || status === 'finished') {
        return status;
    }
    return undefined;
};

const mapStopTypeToApi = (type: StopType | undefined): ApiStopType => {
    if (type === 'load' || type === 'unload' || type === 'execute') {
        return type;
    }
    return 'load';
};

export const mapStop = (stop: ApiBaseStop): Stop => ({
    charging: mapCharging(stop.charging),
    id: stop.id,
    name: stop.name,
    startActual: stop.start_actual,
    startTarget: stop.start_target,
    endActual: stop.end_actual,
    endTarget: stop.end_target,
    location: mapLocation(stop.location, stop.coordinates),
    type: mapStopType(stop.type),
    status: mapStopStatus(stop.status),
    comment: undefined,
    latitude: stop.coordinates?.latitude,
    longitude: stop.coordinates?.longitude,
});

export const mapStopDetails = (stop: ApiDetailedStop): StopDetails => ({
    ...mapStop(stop),
    materials: mapMaterials('materials' in stop && Array.isArray(stop.materials) ? stop.materials : []),
});

export const mapStopToWriteStop = (stop: Stop): ApiWriteStop => ({
    charging: mapChargingToWrite(stop.charging),
    clear_charging: stop.clearCharging,
    name: stop.name,
    location: mapLocationToApi(stop.location),
    start_target: stop.startTarget,
    end_target: stop.endTarget,
    type: mapStopTypeToApi(stop.type),
    coordinates:
        stop.latitude != null && stop.longitude != null
            ? {
                  latitude: stop.latitude,
                  longitude: stop.longitude,
              }
            : undefined,
});

export const mapStopToUpdateStop = (stop: Stop): ApiUpdateStop => ({
    charging: mapChargingToUpdate(stop.charging),
    clear_charging: stop.clearCharging,
    name: stop.name,
    location: mapLocationToApi(stop.location),
    start_target: stop.startTarget,
    end_target: stop.endTarget,
    comment: stop.comment,
    coordinates:
        stop.latitude != null && stop.longitude != null
            ? {
                  latitude: stop.latitude,
                  longitude: stop.longitude,
              }
            : undefined,
});
